import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Trash2, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DateInput } from "@/components/DateInput";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { euro, formatApiErrorDetail } from "@/lib/format";
import { todayIso, fmtDay } from "@/lib/cantieri";

export const OrePanel = ({ cantiere, operai, onChanged }) => {
  const [data, setData] = useState(todayIso());
  const [ore, setOre] = useState({});
  const [list, setList] = useState([]);
  const [all, setAll] = useState(false);
  const [saving, setSaving] = useState(false);
  const load = () => api.get(`/cantieri/${cantiere.id}/ore`).then((r) => setList(r.data));
  useEffect(() => { load(); }, [cantiere.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setOre(Object.fromEntries(list.filter((r) => r.data === data).map((r) => [r.operaio_id, r.ore]))); }, [list, data]);

  const assigned = operai.filter((o) => cantiere.operai_ids?.includes(o.id));
  const shown = all || !assigned.length ? operai.filter((o) => o.attivo !== false) : assigned;
  const save = async () => {
    setSaving(true);
    try {
      const righe = shown.filter((o) => ore[o.id] !== undefined && ore[o.id] !== "").map((o) => ({ operaio_id: o.id, ore: Number(ore[o.id]) || 0 }));
      if (!righe.length) { toast.error("Inserisci le ore di almeno un operaio"); return; }
      const r = await api.post(`/cantieri/${cantiere.id}/ore`, { data, righe });
      toast.success(`Ore salvate per il ${fmtDay(data)}`);
      onChanged(r.data.cantiere); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const del = async (id) => { await api.delete(`/ore-lavorate/${id}`); load(); onChanged(); };

  return (
    <div className="space-y-4" data-testid="ore-panel">
      <div className="rounded-xl border border-border p-4 space-y-3">
        <div className="flex items-end gap-3 flex-wrap">
          <div className="space-y-1.5"><Label className="text-xs">Giorno</Label><DateInput value={data} onChange={(e) => setData(e.target.value)} className="w-44" data-testid="ore-date-input" /></div>
          {assigned.length > 0 && <button type="button" className="text-sm text-primary font-medium pb-2" onClick={() => setAll((v) => !v)} data-testid="ore-toggle-all">{all ? "Solo operai assegnati" : "Mostra tutti gli operai"}</button>}
        </div>
        <p className="text-xs text-muted-foreground">Ogni giorno scegli chi ha lavorato e quante ore: lascia vuoto chi non c'era.</p>
        {shown.length === 0 && <p className="text-sm text-muted-foreground">Aggiungi prima gli operai nella scheda «Operai».</p>}
        <div className="grid sm:grid-cols-2 gap-2">
          {shown.map((o) => (
            <div key={o.id} className="flex items-center gap-2 rounded-lg bg-secondary/40 px-3 py-2">
              <span className="flex-1 text-sm font-medium truncate">{o.nome}</span>
              {[4, 8].map((h) => (
                <button key={h} type="button" onClick={() => setOre((x) => ({ ...x, [o.id]: h }))} className="text-sm min-w-[40px] min-h-[36px] [@media(pointer:coarse)]:min-h-[44px] px-2 rounded-md border border-border hover:bg-card" data-testid={`ore-quick-${h}-${o.id}`}>{h}h</button>
              ))}
              <Input type="number" min="0" max="24" step="0.5" value={ore[o.id] ?? ""} onChange={(e) => setOre((x) => ({ ...x, [o.id]: e.target.value }))} className="w-20 h-9" placeholder="ore" data-testid={`ore-input-${o.id}`} />
            </div>
          ))}
        </div>
        {shown.length > 0 && <div className="flex justify-end"><Button onClick={save} disabled={saving} data-testid="ore-save-button">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" /> Salva ore</>}</Button></div>}
      </div>
      <div className="rounded-xl border border-border divide-y divide-border max-h-72 overflow-y-auto" data-testid="ore-history">
        {list.map((r) => (
          <div key={r.id} className="flex items-center gap-3 px-4 py-2 text-sm group">
            <span className="w-24 text-muted-foreground">{fmtDay(r.data)}</span>
            <span className="flex-1 truncate font-medium">{r.operaio_nome}</span>
            <span className="w-14 text-right">{Number(r.ore).toLocaleString("it-IT")} h</span>
            <span className="w-24 text-right text-muted-foreground">{euro(r.ore * (r.costo_orario || 0))}</span>
            <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={() => del(r.id)} data-testid={`ore-delete-${r.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
          </div>
        ))}
        {list.length === 0 && <div className="p-6 text-center text-sm text-muted-foreground">Nessuna ora registrata.</div>}
      </div>
    </div>
  );
};

export default OrePanel;
